import React, { useState, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { BASE_URL, calculateAge, titleCase, padZero } from '../../utils';

const Superbill = (props) => {
  const [patientData, setPatientData] = useState(null);
  const [primaryInsuranceData, setPrimaryInsuranceData] = useState(null);
  const [secondaryInsuranceData, setSecondaryInsuranceData] = useState(null);
  const [tertiaryInsuranceData, setTertiaryInsuranceData] = useState(null);

  // ------------------------------ PROPS ------------------------------
  const { patients } = props;

  // ------------------------------ Effects Helpers ------------------------------
  const getPatientData = () => {
    if (patients && patients.length > 0) {
      // console.log(`Patient ID = ${patients[0]}`);
      const url = new URL(`${BASE_URL}/patients/getPatientProfile`);
      const params = { id: patients[0] || null };
      url.search = new URLSearchParams(params).toString();

      fetch(url)
        .then(response => response.text())
        .then(result => {
          const reqData = JSON.parse(result);
          // console.log(reqData);
          setPatientData(reqData.patient);
        })
        .catch(error => console.log('error', error));
    }
  };

  const getPayerData = (insuranceType) => {
    if (patients && patients.length > 0) {
      let payerRoute = '';
      switch (insuranceType) {
        case 'primary':
          payerRoute = 'getPatientPrimaryInsuranceData';
          break;
        case 'secondary':
          payerRoute = 'getPatientSecondaryInsuranceData';
          break;
        case 'tertiary':
          payerRoute = 'getPatientTertiaryInsuranceData';
          break;
        default:
          payerRoute = '';
      }

      if (payerRoute === '') {
        return;
      }

      const url = new URL(`${BASE_URL}/patients/${payerRoute}`);
      const params = { patientId: patients[0] };
      url.search = new URLSearchParams(params).toString();
      fetch(url)
        .then(response => response.text())
        .then(result => {
          const reqData = JSON.parse(result);
          if (reqData.payerData && Object.keys(reqData.payerData).length > 0) {
            // console.log(`---------- ${insuranceType} Insurance Data ----------`);
            // console.log(reqData.payerData);
            if (insuranceType === 'primary') {
              setPrimaryInsuranceData(reqData.payerData);
            }
            if (insuranceType === 'secondary') {
              setSecondaryInsuranceData(reqData.payerData);
            }
            if (insuranceType === 'tertiary') {
              setTertiaryInsuranceData(reqData.payerData);
            }
          }
        })
        .catch(error => console.log('error', error));
    }
  };

  // ------------------------------ Effects ------------------------------
  useEffect(() => {
    getPatientData();
  }, [patients]);

  useEffect(() => {
    // console.log('Getting all payers data');
    if (patientData && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0) {
      getPayerData('primary');
    }

    if (patientData && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0) {
      getPayerData('secondary');
    }

    if (patientData && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0) {
      getPayerData('tertiary');
    }
  }, [patientData]);

  // ------------------------------ Styles ----------------------------
  const styles = StyleSheet.create({
    sBillContainer: {
      padding: '8px',
      // background: '#FFFF0080',
      background: '#889349',
      height: '100%',
      width: '100%'
    },
    header: {
      background: 'black',
      height: '32px',
      color: 'white',
      textAlign: 'center'
    },
    sectionHeader: {
      height: '32px',
      background: 'black',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: '700',
      paddingLeft: '8px'
    },
    smallRow: {
      background: 'white',
      height: '24px',
      fontSize: '14px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center'
    },
    sectionTitle: {
      color: '#00000080'
    },
    sectionText: {
      color: '#000'
    },
    sectionTitleLg: {
      width: '189px',
      fontSize: '14px',
      fontWeight: '700',
      background: 'white',
      // color: 'black',
      color: '#00000080',
      height: '40px',
      paddingLeft: '4px',
      display: 'flex',
      alignItems: 'center'
    },
    sectionTitleDetails: {
      height: '40px',
      background: 'white',
      color: 'black',
      paddingLeft: '4px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: '700'
    },
    unitsSection: {
      width: '97px',
      minWidth: '97px',
      maxWidth: '97px',
      fontSize: '14px',
      fontWeight: '700',
      marginLeft: '1px'
    }
  });

  // console.log('----------------------------------------');
  // console.log(props.caseData);
  // console.log('----------------------------------------');

  let patientDobMonth = '';
  let patientDobDate = '';
  let patientDobYear = '';
  let patientAge = '';
  let patientHomeAddress = '';
  let patientMailingAddress = '';

  if (patientData && patientData.dob && patientData.dob.length > 0) {
    const tmp1 = patientData.dob.split('T');
    const tmp2 = tmp1[0].split('-');
    patientDobYear = tmp2[0];
    patientDobMonth = tmp2[1];
    patientDobDate = tmp2[2];
    patientAge = calculateAge(`${patientDobMonth}/${patientDobDate}/${patientDobYear}`);
  }

  if (patientData && patientData.homeAddress && Object.keys(patientData.homeAddress).length > 0) {
    const { streetAddress1, suite, city, state, zip } = patientData.homeAddress;
    patientHomeAddress = `${streetAddress1}${suite.length > 0 ? ` ${suite}` : ''} ${city} ${state} ${zip}`;
  }

  if (patientData && patientData.mailingAddressSameAsHomeAddress) {
    patientMailingAddress = 'Same As Home Address';
  } else if (patientData && patientData.mailingAddress && Object.keys(patientData.mailingAddress).length > 0) {
    const { streetAddress1, suite, city, state, zip } = patientData.mailingAddress;
    patientMailingAddress = `${streetAddress1}${suite.length > 0 ? ` ${suite}` : ''} ${city} ${state} ${zip}`;
  }

  let primaryPayerAddress = '';
  let primaryInsuranceSubscriberDobString = '';
  let secondaryPayerAddress = '';
  let secondaryInsuranceSubscriberDobString = '';
  let tertiaryPayerAddress = '';
  let tertiaryInsuranceSubscriberDobString = '';

  if (primaryInsuranceData && primaryInsuranceData.address && Object.keys(primaryInsuranceData).length > 0) {
    const { streetAddress1, suite, city, state, zip } = primaryInsuranceData.address;
    primaryPayerAddress = `${streetAddress1}${suite.length > 0 ? ` ${suite}` : ''} ${city} ${state} ${zip}`;
  }

  if (patientData && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0 && patientData.primaryInsurance.memberDob.length > 0) {
    const tmp1 = patientData.primaryInsurance.memberDob.split('T');
    const tmp2 = tmp1[0].split('-');
    const _year = tmp2[0];
    const _month = tmp2[1];
    const _date = tmp2[2];
    const _age = calculateAge(`${_month}/${_date}/${_year}`);
    primaryInsuranceSubscriberDobString = `(${_month}/${_date}/${_year}, ${_age})`;
  }

  if (secondaryInsuranceData && secondaryInsuranceData.address && Object.keys(secondaryInsuranceData).length > 0) {
    const { streetAddress1, suite, city, state, zip } = secondaryInsuranceData.address;
    secondaryPayerAddress = `${streetAddress1}${suite.length > 0 ? ` ${suite}` : ''} ${city} ${state} ${zip}`;
  }

  if (patientData && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0 && patientData.secondaryInsurance.memberDob.length > 0) {
    const tmp1 = patientData.secondaryInsurance.memberDob.split('T');
    const tmp2 = tmp1[0].split('-');
    const _year = tmp2[0];
    const _month = tmp2[1];
    const _date = tmp2[2];
    const _age = calculateAge(`${_month}/${_date}/${_year}`);
    secondaryInsuranceSubscriberDobString = `(${_month}/${_date}/${_year}, ${_age})`;
  }

  if (tertiaryInsuranceData && tertiaryInsuranceData.address && Object.keys(tertiaryInsuranceData).length > 0) {
    const { streetAddress1, suite, city, state, zip } = tertiaryInsuranceData.address;
    tertiaryPayerAddress = `${streetAddress1}${suite.length > 0 ? ` ${suite}` : ''} ${city} ${state} ${zip}`;
  }

  if (patientData && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0 && patientData.tertiaryInsurance.memberDob.length > 0) {
    const tmp1 = patientData.tertiaryInsurance.memberDob.split('T');
    const tmp2 = tmp1[0].split('-');
    const _year = tmp2[0];
    const _month = tmp2[1];
    const _date = tmp2[2];
    const _age = calculateAge(`${_month}/${_date}/${_year}`);
    tertiaryInsuranceSubscriberDobString = `(${_month}/${_date}/${_year}, ${_age})`;
  }

  let dosString = '';
  if (props.caseData && props.caseData.dateOfSurgery && props.caseData.dateOfSurgery.length > 0) {
    const tmp1 = props.caseData.dateOfSurgery.split('T');
    const tmp2 = tmp1[0].split('-');
    const _year = tmp2[0];
    const _month = tmp2[1];
    const _date = tmp2[2];
    dosString = `${_month}/${_date}/${_year}`;
  }

  let anesthesiaTime = '';
  if (props.caseData.anesthesiaStartDate && props.caseData.anesthesiaStartDate.length > 0 &&
    props.caseData.anesthesiaEndDate && props.caseData.anesthesiaEndDate.length > 0) {
    const stmp1 = props.caseData.anesthesiaStartDate.split('T');
    const stmp2 = stmp1[0].split('-');
    const _sYear = stmp2[0];
    const _sMonth = stmp2[1];
    const _sDate = stmp2[2];
    const anesthesiaStartDateTime = new Date(
      _sYear,
      _sMonth,
      _sDate,
      props.caseData.anesthesiaStartHour,
      props.caseData.anesthesiaStartMinute
    );

    const etmp1 = props.caseData.anesthesiaEndDate.split('T');
    const etmp2 = etmp1[0].split('-');
    const _eYear = etmp2[0];
    const _eMonth = etmp2[1];
    const _eDate = etmp2[2];
    const anesthesiaEndDateTime = new Date(
      _eYear,
      _eMonth,
      _eDate,
      props.caseData.anesthesiaEndHour,
      props.caseData.anesthesiaEndMinute
    );

    const timeDiff = (anesthesiaEndDateTime.getTime() - anesthesiaStartDateTime.getTime()) / (1000 * 60);
    anesthesiaTime = timeDiff.toFixed(0);
  }

  return (
    <div id='sbill' className={css(styles.sBillContainer)}>
      {/* Case Header */}
      <div className={css(styles.header)}>
        Anesthesia Billing Summary: Case #987654321
      </div>
      {/* Patient Section */}
      <div className={css(styles.patientSection)}>
        {/* Patient Name, DOB and Gender */}
        <div className={`fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}>
          {/* Full Name */}
          <div className={css(styles.sectionTitle)}>Patient</div>
          {patientData &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {`${patientData.lastName}, ${patientData.firstName} ${patientData.middleName}`}
            </div>
          }
          {/* DOB */}
          <div className={`ml-4 ${css(styles.sectionTitle)}`}>DOB</div>
          {patientDobYear.length > 0 && patientDobMonth.length > 0 && patientDobDate.length > 0 &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {`${patientDobMonth}/${patientDobDate}/${patientDobYear}`}
            </div>
          }
          {/* Age */}
          <div className={`ml-4 ${css(styles.sectionTitle)}`}>Age</div>
          {patientAge.length > 0 &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {patientAge}
            </div>
          }
          {/* Gender */}
          <div className={`ml-4 ${css(styles.sectionTitle)}`}>Gender</div>
          {patientData && patientData.gender.length > 0 &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {`${patientData.gender.toLowerCase().startsWith('male') ? 'Male' : 'Female'}`}
            </div>
          }
        </div>
        {/* Patient Home and Mailing Address */}
        <div className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}>
          {/* Home Address */}
          <div className={css(styles.sectionTitle)}>Home</div>
          {patientData && patientHomeAddress.length > 0 &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {patientHomeAddress}
            </div>
          }
          {/* Mailing Address */}
          <div className={`ml-4 ${css(styles.sectionTitle)}`}>Mailing</div>
          {patientData && patientMailingAddress.length > 0 &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {patientMailingAddress}
            </div>
          }
        </div>
        {/* Patient Mobile, Phone & Email */}
        <div className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}>
          {/* Mobile */}
          <div className={css(styles.sectionTitle)}>Mobile#</div>
          {patientData && patientData.mobile && patientData.mobile.length > 0 && patientData.contactType === 'mobile' &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {patientData.mobile}
            </div>
          }
          {/* Phone */}
          <div className={`ml-4 ${css(styles.sectionTitle)}`}>Phone#</div>
          {patientData && patientData.mobile && patientData.mobile.length > 0 && patientData.contactType !== 'mobile' &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {patientData.mobile}
            </div>
          }
          {/* Email */}
          <div className={`ml-4 ${css(styles.sectionTitle)}`}>Email</div>
          {patientData && patientData.email && patientData.email.length > 0 &&
            <div className={`ml-2 ${css(styles.sectionText)}`}>
              {patientData.email}
            </div>
          }
        </div>
      </div>
      {/* Primary Insurance */}
      <div className={`mt1 ${css(styles.sectionHeader)}`}>
        Primary Insurance:&nbsp;
        {primaryInsuranceData && Object.keys(primaryInsuranceData).length > 0
          ? `${primaryInsuranceData.payerName}${primaryPayerAddress.length > 0 ? ` ${primaryPayerAddress}` : ''}`
          : 'None'
        }
      </div>
      {/* Phone, Email, Subscriber and Relation to Subscriber */}
      <div
        className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}
        style={{ display: primaryInsuranceData && Object.keys(primaryInsuranceData).length > 0 ? '' : 'none' }}
      >
        {/* Phone */}
        <div className={css(styles.sectionTitle)}>Phone#</div>
        {primaryInsuranceData && primaryInsuranceData.phone && primaryInsuranceData.phone.length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {primaryInsuranceData.phone}
          </div>
        }
        {/* Email */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Email</div>
        {primaryInsuranceData && primaryInsuranceData.email && primaryInsuranceData.email.length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {primaryInsuranceData.email}
          </div>
        }
        {/* Subscriber */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Subscriber</div>
        {patientData && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {`${patientData.primaryInsurance.memberLastName}, ${patientData.primaryInsurance.memberFirstName}${patientData.primaryInsurance.memberMiddleName ? ` ${patientData.primaryInsurance.memberMiddleName}` : ''}${primaryInsuranceSubscriberDobString}`}
          </div>
        }
        {/* Rel2Pt */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Rel2Pt</div>
        {patientData && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.primaryInsurance.patientRelationToMember}
          </div>
        }
      </div>
      {/* Primary Insurance Policy#, Auth# and Group# */}
      <div
        className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}
        style={{ display: primaryInsuranceData && Object.keys(primaryInsuranceData).length > 0 ? '' : 'none' }}
      >
        {/* Policy# */}
        <div className={css(styles.sectionTitle)}>Policy#</div>
        {patientData && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.primaryInsurance.policyNumber}
          </div>
        }
        {/* Auth# */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Auth#</div>
        {patientData && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.primaryInsurance.authorizationNumber}
          </div>
        }
        {/* Group# */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Group#</div>
        {patientData && patientData.primaryInsurance && Object.keys(patientData.primaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.primaryInsurance.groupNumber}
          </div>
        }
      </div>
      {/* Secondary Insurance */}
      <div className={`mt1 ${css(styles.sectionHeader)}`}>
        Secondary Insurance:&nbsp;
        {secondaryInsuranceData && Object.keys(secondaryInsuranceData).length > 0
          ? `${secondaryInsuranceData.payerName}${secondaryPayerAddress.length > 0 ? ` ${secondaryPayerAddress}` : ''}`
          : 'None'
        }
      </div>
      {/* Secondary Insurance - Phone, Email, Subscriber and Relation to Subscriber */}
      <div
        className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}
        style={{ display: secondaryInsuranceData && Object.keys(secondaryInsuranceData).length > 0 ? '' : 'none' }}
      >
        {/* Phone */}
        <div className={css(styles.sectionTitle)}>Phone#</div>
        {secondaryInsuranceData && secondaryInsuranceData.phone && secondaryInsuranceData.phone.length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {secondaryInsuranceData.phone}
          </div>
        }
        {/* Email */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Email</div>
        {secondaryInsuranceData && secondaryInsuranceData.email && secondaryInsuranceData.email.length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {secondaryInsuranceData.email}
          </div>
        }
        {/* Subscriber */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Subscriber</div>
        {patientData && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {`${patientData.secondaryInsurance.memberLastName}, ${patientData.secondaryInsurance.memberFirstName}${patientData.secondaryInsurance.memberMiddleName ? ` ${patientData.secondaryInsurance.memberMiddleName}` : ''}${secondaryInsuranceSubscriberDobString}`}
          </div>
        }
        {/* Rel2Pt */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Rel2Pt</div>
        {patientData && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.secondaryInsurance.patientRelationToMember}
          </div>
        }
      </div>
      {/* Secondary Insurance Policy#, Auth# and Group# */}
      <div
        className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}
        style={{ display: secondaryInsuranceData && Object.keys(secondaryInsuranceData).length > 0 ? '' : 'none' }}
      >
        {/* Policy# */}
        <div className={css(styles.sectionTitle)}>Policy#</div>
        {patientData && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.secondaryInsurance.policyNumber}
          </div>
        }
        {/* Auth# */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Auth#</div>
        {patientData && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.secondaryInsurance.authorizationNumber}
          </div>
        }
        {/* Group# */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Group#</div>
        {patientData && patientData.secondaryInsurance && Object.keys(patientData.secondaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.secondaryInsurance.groupNumber}
          </div>
        }
      </div>
      {/* Tertiary Insurance */}
      <div className={`mt1 ${css(styles.sectionHeader)}`}>
        Tertiary Insurance:&nbsp;
        {tertiaryInsuranceData && Object.keys(tertiaryInsuranceData).length > 0
          ? `${tertiaryInsuranceData.payerName}${tertiaryPayerAddress.length > 0 ? ` ${tertiaryPayerAddress}` : ''}`
          : 'None'
        }
      </div>
      {/* Tertiary Insurance - Phone, Email, Subscriber and Relation to Subscriber */}
      <div
        className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}
        style={{ display: tertiaryInsuranceData && Object.keys(tertiaryInsuranceData).length > 0 ? '' : 'none' }}
      >
        {/* Phone */}
        <div className={css(styles.sectionTitle)}>Phone#</div>
        {tertiaryInsuranceData && tertiaryInsuranceData.phone && tertiaryInsuranceData.phone.length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {tertiaryInsuranceData.phone}
          </div>
        }
        {/* Email */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Email</div>
        {tertiaryInsuranceData && tertiaryInsuranceData.email && tertiaryInsuranceData.email.length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {tertiaryInsuranceData.email}
          </div>
        }
        {/* Subscriber */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Subscriber</div>
        {patientData && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {`${patientData.tertiaryInsurance.memberLastName}, ${patientData.tertiaryInsurance.memberFirstName}${patientData.tertiaryInsurance.memberMiddleName ? ` ${patientData.tertiaryInsurance.memberMiddleName}` : ''}${tertiaryInsuranceSubscriberDobString}`}
          </div>
        }
        {/* Rel2Pt */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Rel2Pt</div>
        {patientData && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.tertiaryInsurance.patientRelationToMember}
          </div>
        }
      </div>
      {/* Tertiary Insurance Policy#, Auth# and Group# */}
      <div
        className={`mt1 fx fx__v-center pl-2 pr-2 ${css(styles.smallRow)}`}
        style={{ display: tertiaryInsuranceData && Object.keys(tertiaryInsuranceData).length > 0 ? '' : 'none' }}
      >
        {/* Policy# */}
        <div className={css(styles.sectionTitle)}>Policy#</div>
        {patientData && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.tertiaryInsurance.policyNumber}
          </div>
        }
        {/* Auth# */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Auth#</div>
        {patientData && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.tertiaryInsurance.authorizationNumber}
          </div>
        }
        {/* Group# */}
        <div className={`ml-4 ${css(styles.sectionTitle)}`}>Group#</div>
        {patientData && patientData.tertiaryInsurance && Object.keys(patientData.tertiaryInsurance).length > 0 &&
          <div className={`ml-2 ${css(styles.sectionText)}`}>
            {patientData.tertiaryInsurance.groupNumber}
          </div>
        }
      </div>
      {/* Self Pay */}
      <div className={`mt1 ${css(styles.sectionHeader)}`}>
        Self Pay Method: None
      </div>
      {/* Date of Service, Patient Type and Facility */}
      <div className='fx'>
        <div className={css(styles.sectionTitleLg)}>
          DOS&nbsp;
          <span style={{ color: '#000' }}>
            {`${dosString.length > 0 ? ` ${dosString}` : ''}`}
          </span>
        </div>
        <div className={css(styles.sectionTitleDetails)}>
          {`Admission Type ${props.caseData.patientType.toLowerCase() === 'inpatient' ? 'In Patient' : 'Out Patient'}`}
        </div>
      </div>
      {/* Surgeon(s) */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Surgeon(s)
        </div>
        <div style={{ width: '100%' }}>
          {props.caseData.surgeons.map((el, idx) => {
            return (
              <div
                key={el.user}
                className={css(styles.sectionTitleDetails)}
              >
                {`${idx + 1}. ${el.fullName} / NPI ${el.npi}`}
              </div>
            );
          })}
        </div>
      </div>
      {/* Procedures(s) */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Procedure(s)
        </div>
        <div style={{ width: '100%' }}>
          {props.caseData.surgicalProcedures.map((el, idx) => {
            return (
              <div
                key={`${el._id}_surg_proc`}
                className={css(styles.sectionTitleDetails)}
              >
                {`${idx + 1}. ${el.description}`}
              </div>
            );
          })}
        </div>
      </div>
      {/* PreOp Diagnosis */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          PreOp Diagnosis
        </div>
        <div style={{ width: '100%' }}>
          {props.caseData.preOpDx.length > 0 && props.caseData.preOpDx.map((el, idx) => {
            return (
              <div
                key={`${el._id}_preop_dx`}
                className={css(styles.sectionTitleDetails)}
              >
                {`${idx + 1}. ${el.description}`}
              </div>
            );
          })}
          {props.caseData.otherPreOpDx.length > 0 && props.caseData.otherPreOpDx.map((el, idx) => {
            return (
              <div
                key={el.id}
                className={css(styles.sectionTitleDetails)}
              >
                {`${props.caseData.preOpDx.length + idx + 1}. ${el.description}`}
              </div>
            );
          })}
        </div>
      </div>
      {/* PostOp Diagnosis */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          PostOp Diagnosis
        </div>
        <div style={{ width: '100%' }}>
          {props.caseData.isSameAsPreOpDx &&
            <div className={css(styles.sectionTitleDetails)}>
              Same As PreOp Diagnosis
            </div>
          }
          {!props.caseData.isSameAsPreOpDx && props.caseData.postOpDx.length > 0 && props.caseData.postOpDx.map((el, idx) => {
            return (
              <div
                key={el._id}
                className={css(styles.sectionTitleDetails)}
              >
                {`${idx + 1}. ${el.description}`}
              </div>
            );
          })}
          {!props.caseData.isSameAsPreOpDx && props.caseData.otherPostOpDx.length > 0 && props.caseData.otherPostOpDx.map((el, idx) => {
            return (
              <div
                key={el.id}
                className={css(styles.sectionTitleDetails)}
              >
                {`${props.caseData.postOpDx.length + idx + 1}. ${el.description}`}
              </div>
            );
          })}
        </div>
      </div>
      {/* Anesthesia Providers(s) -- including Anesthesiologist(s) & CRNA(s) */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Anesthesia Provider(s)
        </div>
        <div style={{ width: '100%' }}>
          {props.caseData.anesthesiologists.map((el, idx) => {
            return (
              <div
                key={el.user}
                className={css(styles.sectionTitleDetails)}
              >
                {`${idx + 1}. ${titleCase(el.fullName)} / NPI ${el.npi}`}
              </div>
            );
          })}
          {props.caseData.crnas.map((el, idx) => {
            return (
              <div
                key={el.user}
                className={css(styles.sectionTitleDetails)}
              >
                {`${props.caseData.anesthesiologists.length + idx + 1}. ${titleCase(el.fullName)} / NPI ${el.npi}`}
              </div>
            );
          })}
        </div>
      </div>
      {/* Anesthesia Service */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Anesthesia Service
        </div>
        <div className='fx' style={{ width: '100%' }}>
          <div style={{ flexGrow: '1' }}>
            <div className={css(styles.sectionTitleDetails)} />
          </div>
          <div className={css(styles.unitsSection, styles.sectionTitleDetails)}>
            {/* 555.55 Units */}
          </div>
        </div>
      </div>
      {/* Anesthesia Times */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Anesthesia Times
        </div>
        <div className='fx' style={{ width: '100%' }}>
          <div
            className={css(styles.sectionTitleDetails)}
            style={{ flexGrow: '1' }}
          >
            <div className='fx'>
              <div className={css(styles.sectionTitle)}>Start</div>
              <div className='ml-2'>
                {`${padZero(props.caseData.anesthesiaStartHour)}${padZero(props.caseData.anesthesiaStartMinute)}`}
              </div>
            </div>
            <div className='ml-3 fx'>
              <div className={css(styles.sectionTitle)}>Stop</div>
              <div className='ml-2'>
                {`${padZero(props.caseData.anesthesiaEndHour)}${padZero(props.caseData.anesthesiaEndMinute)}`}
              </div>
            </div>
            <div className='ml-3 fx'>
              <div className={css(styles.sectionTitle)}>Total</div>
              <div className='ml-2'>
                {`${anesthesiaTime} minutes`}
              </div>
            </div>
            <div className='ml-4'>
              12min = 1u
            </div>
          </div>
          <div className={css(styles.unitsSection, styles.sectionTitleDetails)}>
            {/* 555.55 Units */}
          </div>
        </div>
      </div>
      {/* ASA Score */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          ASA Score
        </div>
        <div className='fx' style={{ width: '100%' }}>
          <div style={{ flexGrow: '1' }}>
            <div className={css(styles.sectionTitleDetails)}>
              {props.caseData.asaScore}
            </div>
          </div>
          <div className={css(styles.unitsSection, styles.sectionTitleDetails)}>
            {/* 4 Units */}
          </div>
        </div>
      </div>
      {/* Anesthesia Type */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Anesthesia Type
        </div>
        <div className='fx' style={{ width: '100%' }}>
          <div style={{ flexGrow: '1' }}>
            <div className={css(styles.sectionTitleDetails)}>
              {props.caseData.anesthesiaTypes.join(' + ')}
            </div>
          </div>
          <div className={css(styles.unitsSection, styles.sectionTitleDetails)}>
            {/* 4 Units */}
          </div>
        </div>
      </div>
      {/* Anesthesia Procedure(s) */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Anesthesia Procedure(s)
        </div>
        <div className='fx' style={{ width: '100%' }}>
          <div style={{ width: '100%' }}>
            {props.caseData.anesthesiaProcedures.map((el, idx) => {
              return (
                <div
                  key={`${el._id}_anestehsia_proc`}
                  className='fx'
                  style={{ width: '100%' }}
                >
                  <div
                    className={css(styles.sectionTitleDetails)}
                    style={{ flexGrow: '1' }}
                  >
                    {`${idx + 1}. ${el.description}`}
                  </div>
                  <div className={css(styles.unitsSection, styles.sectionTitleDetails)}>
                    {/* {`${idx + 2} Units`} */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Anesthesia Modifier(s) */}
      <div className='fx mt1'>
        <div className={css(styles.sectionTitleLg)}>
          Anesthesia Modifier(s)
        </div>
        <div className='fx' style={{ width: '100%' }}>
          <div style={{ width: '100%' }}>
            {props.caseData.anesthesiaModifiers.map((el, idx) => {
              return (
                <div
                  key={Math.random().toString(36).slice(2)}
                  className='fx'
                  style={{ width: '100%' }}
                >
                  <div
                    className={css(styles.sectionTitleDetails)}
                    style={{ flexGrow: '1' }}
                  >
                    {`${idx + 1}. ${el}`}
                  </div>
                  <div className={css(styles.unitsSection, styles.sectionTitleDetails)}>
                    {/* {`${idx + 2} Units`} */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Superbill;
